import React from 'react';
import {
	Typography,
} from 'antd';
import {
	Currencies,
	FuelTypes,
	HireInvoiceItemStates,
	VoyageBunkerTypeLabels,
	VoyageBunkerTypes,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import { formatCurrency } from '@shared/utils/currency';
import { capitalize, formatQuantity } from '@shared/utils/string';
import type { VoyageBunkerEntry } from '@api/utils/sequelize/getVoyageBunkers';
import type { FormattedAttachments } from '@api/utils/sequelize/formatAttachments';
import EstimatedActualTag from '@client/components/EstimatedActualTag';
import StateTag from '@client/components/StateTag';

export type BunkerSide = (
	| typeof VoyageBunkerTypes.DELIVERY |
	typeof VoyageBunkerTypes.REDELIVERY
);

export const toPnlPrice = (entry: VoyageBunkerEntry): number => (
	entry.pricePerTon / (entry.exchangeRate || 1)
);

export type SingleBunkerRow = {
	key: string;
	id: number | null;
	type: Values<typeof VoyageBunkerTypes> | null;
	fuelGrade: Values<typeof FuelTypes> | null;
	quantity: number | null;
	minQuantity: number | null;
	maxQuantity: number | null;
	price: number | null;
	estimated: boolean | null;
	persistedEstimated: boolean | null;
	state: Values<typeof HireInvoiceItemStates> | null;
	locked: boolean;
	internalNote?: string | null;
	attachments?: FormattedAttachments;
};

export const otherTypeOptions = [
	{
		label: capitalize(VoyageBunkerTypeLabels[VoyageBunkerTypes.OWNERS_ACCOUNT]),
		value: VoyageBunkerTypes.OWNERS_ACCOUNT,
	},
	{
		label: capitalize(VoyageBunkerTypeLabels[VoyageBunkerTypes.CHARTERERS_ACCOUNT]),
		value: VoyageBunkerTypes.CHARTERERS_ACCOUNT,
	},
];

export const getRows = (
	bunkers: VoyageBunkerEntry[],
	{ includeNoteAndAttachments = false }: { includeNoteAndAttachments?: boolean } = {},
): SingleBunkerRow[] => (
	bunkers.map((b) => ({
		key: String(b.id),
		id: b.id,
		type: b.type,
		fuelGrade: b.fuelGrade,
		quantity: b.quantity ?? null,
		minQuantity: b.Bunker?.minQuantity ?? null,
		maxQuantity: b.Bunker?.maxQuantity ?? null,
		price: toPnlPrice(b),
		estimated: b.estimated ?? null,
		persistedEstimated: b.estimated ?? null,
		state: b.state ?? null,
		locked: (b.state !== HireInvoiceItemStates.PENDING || b.disableEdit),
		...(includeNoteAndAttachments ? {
			internalNote: b.internalNote ?? null,
			attachments: b.attachments ?? [],
		} : {}),
	}))
);

const isEditableRow = (row?: SingleBunkerRow) => (
	!row || row.id == null || Boolean(row.persistedEstimated)
);

export const getBunkerTableColumns = ({
	variant,
	currency,
	fuelGrades,
	showInternalNote = false,
}: {
	variant: 'other' | 'single';
	currency: Values<typeof Currencies>;
	allowGradeEdit?: boolean;
	fuelGrades?: Values<typeof FuelTypes>[];
	showInternalNote?: boolean;
}) => {
	const gradeOptions = (fuelGrades ?? Object.values(FuelTypes)).map((grade) => ({
		label: grade,
		value: grade,
	}));

	return [
		{
			dataIndex: 'fuelGrade',
			title: 'Grade',
			width: 120,
			editable: isEditableRow,
			editingProps: {
				type: 'select',
				required: true,
				options: gradeOptions,
				inputProps: { showSearch: true, fullWidth: true },
			},
			render: (v: string | null) => v ?? '',
		},
		...(variant === 'other' ? [{
			dataIndex: 'type',
			title: 'Type',
			width: 160,
			editable: isEditableRow,
			editingProps: {
				type: 'select',
				required: true,
				options: otherTypeOptions,
				inputProps: { fullWidth: true },
			},
			render: (t: Values<typeof VoyageBunkerTypes> | null) => (
				t == null ? '' : capitalize(VoyageBunkerTypeLabels[t as keyof typeof VoyageBunkerTypeLabels])
			),
		}] : []),
		{
			dataIndex: 'estimated',
			title: 'Est/Act',
			align: 'center',
			width: 90,
			editable: isEditableRow,
			editingProps: {
				type: 'select',
				defaultValue: true,
				options: [
					{ label: 'Est.', value: true },
					{ label: 'Act.', value: false },
				],
				inputProps: { fullWidth: true },
			},
			render: (v: boolean | null) => (v == null ? '' : <EstimatedActualTag estimated={v} />),
		},
		{
			dataIndex: 'quantity',
			title: 'Operational quantity',
			align: 'right',
			width: 115,
			editable: isEditableRow,
			editingProps: { type: 'number', required: true, },
			render: (q: number) => q ? formatQuantity(q, 'MT') : '-',
		},
		{
			dataIndex: 'minQuantity',
			title: 'Min. quantity',
			align: 'right',
			width: 115,
			editable: isEditableRow,
			editingProps: { type: 'number' },
			render: (q: number) => q ? formatQuantity(q, 'MT') : '-',
		},
		{
			dataIndex: 'maxQuantity',
			title: 'Max. quantity',
			align: 'right',
			width: 115,
			editable: isEditableRow,
			editingProps: { type: 'number' },
			render: (q: number) => q ? formatQuantity(q, 'MT') : '-',
		},
		{
			dataIndex: 'price',
			title: `Price (${currency})`,
			align: 'right',
			width: 125,
			editable: true,
			editingProps: { type: 'currency', required: true, inputProps: { currency, addCurrencyBefore: true } },
			render: (v: number | null) => (v == null ? '' : formatCurrency(v, currency)),
		},
		...(showInternalNote ? [{
			dataIndex: 'internalNote',
			title: 'Note',
			width: 200,
			editable: true,
			editingProps: {
				type: 'string',
				inputProps: { fullWidth: true },
			},
			render: (note: string | null) => (
				note == null || note === '' ? (
					''
				) : (
					<Typography.Text
						ellipsis
						style={{ width: 180 }}
					>
						{note}
					</Typography.Text>
				)
			),
		}] : []),
		{
			dataIndex: 'state',
			title: 'Status',
			align: 'center',
			width: 100,
			render: (v: Values<typeof HireInvoiceItemStates> | null) => (
				v == null ? '' : <StateTag state={v} />
			),
		},
	];
};
